import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Sort } from '@angular/material/sort';
import { MatTable } from '@angular/material/table';
import { ReassignKamDialogComponent } from '@app/region-management/dialog/reassign-kam-dialog/reassign-kam-dialog.component';
import { NotificationDialogComponent } from '@app/shared/dialog/notification-dialog/notification-dialog.component';
import { ActionType, IdpName, Roles } from '@app/shared/enums';
import { LdapService } from '@app/shared/services/api/ldap.service';
import { MunicipalityService } from '@app/shared/services/api/municipality.service';
import { UserService } from '@app/shared/services/api/user.service';
import { AuthService } from '@app/shared/services/auth.service';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { InviteUsersDialogComponent } from '@app/user-list/dialog/invite-users-dialog/invite-users-dialog.component';
import { UserDelegateDialogComponent } from '@app/user-list/dialog/user-delegate-dialog/user-delegate-dialog.component';
import { cssVariables } from '@env/css-vars';
import { forEach, isNil, orderBy, split, toLower, trim } from 'lodash';
import { of, throwError } from 'rxjs';
import { catchError, concatMap, mergeMap, switchMap } from 'rxjs/operators';
import { UserListRow } from '../../models/user-list-row';
import { HelperService } from '../../services/helper.service';
import { User } from '@app/shared/models/user';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-user-list-table',
  templateUrl: './user-list-table.component.html',
  styleUrls: ['./user-list-table.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class UserListTableComponent implements OnInit, OnChanges {
  @ViewChild(MatTable, ) table?: MatTable<UserListRow>;
  @Input() data!: any;
  @Output() sortChange: EventEmitter<Sort> = new EventEmitter();
  @Output() reload: EventEmitter<void> = new EventEmitter();

  columnsToDisplay: string[] = ['status', 'email', 'invitationDate', 'registrationDate', 'lastLoginDate', 'rolesName', 'privilegeAccess', 'kommune'];

  constructor(
    public helperService: HelperService,
    public ldapService: LdapService,
    public overlayLoader: OverlayLoaderService,
    public userService: UserService,
    public matDialog: MatDialog,
    public authService: AuthService,
    public municipalityService: MunicipalityService,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    if (this.authService.isSystemAdmin || this.authService.isKAM || this.authService.isBackOffice) {
      this.columnsToDisplay.push('delegate');
    }
    if (this.authService.permissions.USER_MANAGEMENT_EDIT) {
      this.columnsToDisplay.push('edit');
    }
    this.columnsToDisplay.push('delete');
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data']) {
      const userManagedRoles = split(this.authService.currentUser.managedRoles, ',');
      forEach(this.data, (u) => {
        u.canManage = userManagedRoles.find((a) => trim(toLower(a)) === trim(toLower(u.rolesCode)));
        u.showMore = false;
        u.isDelegationActive = !isNil(u.delegateEmail);
        const hasAllMunicipalities =
          u.rolesCode === Roles.systemAdministrator ||
          u.rolesCode === Roles.callCenterAgent ||
          u.rolesCode === Roles.meterPointExpert ||
          u.rolesCode === Roles.poDSO;

        const orderedRegions = u.regions
          ? orderBy(
              trim(u.regions.replace(/,\s/g, '*'))
                .split(',')
                .map((x) => x.replace('*', ', ')),
              (a) => a,
              ['asc']
            )
          : '';

        const municipalityWorkerRegions = u.rolesCode === Roles.municipalityWorker ? [u.municipalityName] : orderedRegions;

        u.kommune = hasAllMunicipalities ? ['Alle Gemeinden'] : municipalityWorkerRegions;
      });
    }
  }

  removeUser(element: any) {
    if (!element.canManage) {
      return;
    }

    this.overlayLoader.isLoading = true;
    this.municipalityService
      .getByPrimaryKAM(element.guid)
      .pipe(catchError(() => of([])))
      .subscribe((data) => {
        this.overlayLoader.isLoading = false;
        if (data.length > 0) {
          this.openKamReassignModal(
            element,
            'Dieser KAM ist Kommunen zugeordnet. Bitte wählen Sie zuerst einen neuen verantwortlichen KAM, bevor Sie den User löschen können.'
          );
        } else {
          this.delete(element);
        }
      });
  }

  delete(element: UserListRow) {
    const matRef = this.matDialog.open(NotificationDialogComponent, {
      data: {
        title: 'Löschung des Nutzers bestätigen',
        descriptionText: '<span>Wollen Sie den Nutzer <strong>' + element.email + '</strong> wirklich löschen?</span>',
        descriptionFaClass: 'fa-trash fa-2x',
        confirmText: 'Löschen',
        declineText: 'Abbrechen',
      },
      panelClass: ['-md'],
    });

    matRef.afterClosed().subscribe((res) => {
      if (res) {
        let delete$;
        const idpName = (element.idpname || '').toLocaleUpperCase();
        const deactivateUser$ = this.userService.deactivateUser(element.guid, this.authService.currentUser.email, this.authService.currentUser.roleName).pipe(
          catchError((err) => {
            this.showUserDeactivateErrorNotification();
            return throwError(() => err);
          })
        );

        if (idpName === IdpName.CIAM && element.userId) {
          delete$ = this.userService.createServiceBusPayload({ ...element, action: ActionType.delete }).pipe(
            switchMap((payload) => this.userService.postMessageToServiceBus(element.userId, payload)),
            switchMap(() => deactivateUser$),
            concatMap(() => this.userService.deleteCIAMUser(element.userId)),
            catchError((err) => {
              this.showCiamDeleteErrorNotification();
              return throwError(() => err);
            })
          );
        } else if (idpName === IdpName.AAD && element.userId) {
          delete$ = this.userService
            .createServiceBusPayload({ ...element, action: ActionType.delete, rolesCode: 'eon_internal', municipalities: [{ municipalityKey: '', name: '' }] })
            .pipe(
              switchMap((payload) => this.userService.postMessageToServiceBus(element.userId, payload)),
              catchError((err) => {
                return throwError(() => err);
              }),
              mergeMap(() => deactivateUser$)
            );
        } else {
          delete$ = deactivateUser$;
        }

        if (delete$) {
          this.overlayLoader.isLoading = true;
          delete$.subscribe(() => {
            this.overlayLoader.isLoading = false;
            this.matDialog
              .open(NotificationDialogComponent, {
                data: {
                  descriptionText: 'Nutzer wurde aus dem EnergiePortal entfernt.',
                  descriptionFaClass: 'fa-check-circle fa-2x',
                  descriptionFaColor: cssVariables['--ep-primary-color'],
                  hideClose: true,
                },
                panelClass: ['-md'],
              })
              .afterClosed()
              .subscribe(() => {
                this.reload.emit();
              });
          });
        }
      }
    });
  }

  showUserDeactivateErrorNotification() {
    this.overlayLoader.isLoading = false;
    this.matDialog.open(NotificationDialogComponent, {
      data: {
        descriptionText: 'Löschen fehlgeschlagen. Bitte versuchen Sie es später noch einmal.',
        orientation: 'row',
        alignment: 'start',
      },
      panelClass: ['-md'],
    });
  }

  showCiamDeleteErrorNotification() {
    this.overlayLoader.isLoading = false;
    this.matDialog.open(NotificationDialogComponent, {
      data: {
        title: 'Fehler 504',
        descriptionText: 'Der CIAM-Service ist zurzeit nicht erreichbar, bitte versuchen Sie es später erneut.',
        descriptionFaClass: 'fa-exclamation-triangle fa-2x',
        descriptionFaColor: cssVariables['--ep-primary-color'],
        alignment: 'start',
      },
      panelClass: ['-md'],
    });
  }

  delegate(user: UserListRow) {
    if (this.delegateDisabled(user)) {
      return;
    }
    this.openKamDelegateModal(user);
  }

  openKamDelegateModal(user: { [key: string]: any }) {
    this.matDialog
      .open(UserDelegateDialogComponent, {
        panelClass: ['-md'],
        data: {
          selectedUserGuid: user.guid,
          delegateEmail: user.delegateEmail,
        },
      })
      .afterClosed()
      .subscribe((delegate) => {
        if (delegate) {
          this.userService
            .patch(user.guid, {
              delegateUserId: delegate.guid,
            } as Partial<User>)
            .subscribe((a) => {
              user.isDelegationActive = true;
              user['delegateFirstName'] = delegate.firstname;
              user['delegateLastName'] = delegate.lastname;
              user['delegateEmail'] = delegate.email;
            });
        } else {
          user.isDelegationActive = !isNil(user['delegateEmail']);
        }
      });
  }

  toggleDelegate(isChecked: boolean, user: { [key: string]: any }) {
    if (isChecked) {
      this.openKamDelegateModal(user);
    } else {
      this.userService
        .patch(user.guid, {
          delegateUserId: null,
        } as Partial<User>)
        .subscribe((a) => {
          user.isDelegationActive = false;
          user['delegateFirstName'] = null;
          user['delegateLastName'] = null;
          user['delegateEmail'] = null;
        });
    }
  }

  delegateDisabled(user: UserListRow) {
    if (this.authService.isKAM || this.authService.isBackOffice) {
      if (this.authService.currentUser.guid !== user.guid) {
        return true;
      } else {
        return false;
      }
    } else if (this.authService.currentUser.roleCode === Roles.systemAdministrator) {
      if (user.rolesCode === Roles.keyAccountManager || user.rolesCode === Roles.backOffice) {
        return false;
      } else {
        return true;
      }
    } else {
      return true;
    }
  }

  sortData(sort: Sort) {
    this.sortChange.emit(sort);
  }

  editUser(element: any) {
    if (!element.canManage) {
      return;
    }

    this.matDialog
      .open(InviteUsersDialogComponent, {
        panelClass: ['-md'],
        data: {
          inviteUserDialogTitle: this.translate.instant('module.modals.inviteWorker.editUserDialogTitle'),
          user: {
            ...element,
            roleCode: element.rolesCode,
            roleName: element.rolesName,
            municipalityId: element.assignedMunicipalityId,
            idpName: element.idpname,
            isPrivilegeAccess: element.isPriviledgedAccess,
          },
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result === 'reload') {
          this.reload.emit();
        }

        if (result === 'kam-reassign') {
          this.openKamReassignModal(
            element,
            'Dieser KAM ist Kommunen zugeordnet. Bitte wählen Sie zuerst einen neuen verantwortlichen KAM, bevor Sie den User löschen können.'
          );
        }
      });
  }

  private openKamReassignModal(data: User, message: string) {
    this.matDialog
      .open(ReassignKamDialogComponent, {
        data: {
          currKamName: `${data.firstname} ${data.lastname}`,
          currKamGuid: data.guid,
          message: message,
        },
        panelClass: ['-md'],
      })
      .afterClosed()
      .subscribe((res) => {
        if (res) {
          this.showReassignSuccess();
        }
      });
  }

  showReassignSuccess() {
    this.matDialog.open(NotificationDialogComponent, {
      data: {
        descriptionText: 'KAM-Zuordnung erfolgreich',
        descriptionFaClass: 'fa-check-circle fa-2x',
        descriptionFaColor: cssVariables['--confirm-success-color'],
        hideClose: true,
      },
      panelClass: ['-md'],
    });
  }
}
